import { Divider, Input, Textarea } from '@heroui/react';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import AddressForm from '@/app/components/address-form.tsx';
import { PhoneInput } from '@/app/components/phone-input.tsx';
import { SpecialtiesSelect } from '@/app/components/specialties-select.tsx';
import type { AccountModel } from '../../../types/account.type.ts';
import { emailPattern } from '../../../types/constants.ts';
import { AccountTypeSelect } from './account-type-select.tsx';

export interface AccountFormProps {
  account: AccountModel;
}

const AccountFormFields: FC<AccountFormProps> = ({ account }) => {
  const { watch, control } = useFormContext<AccountModel>();

  const showOtherField = watch('accountType')?.description === 'Other';
  const mealInstructions = watch('mealInstructions') ?? '';
  const mealInstructionsLength = mealInstructions.length;

  const renderSectionDivider = (label: string) => (
    <div className='relative mt-12 mb-6'>
      <Divider />
      <span className='absolute left-0 top-0 -translate-y-1/2 bg-white pr-2 text-sm text-primary-500 font-semibold uppercase'>
        {label}
      </span>
    </div>
  );

  return (
    <div className='flex flex-col max-w-screen-lg self-center w-full'>
      {renderSectionDivider('Account Details')}
      <div className='flex flex-col gap-4'>
        <Controller
          name='accountName'
          control={control}
          rules={{ required: 'This field is required' }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label='Name'
              variant='bordered'
              isRequired
              isInvalid={fieldState.invalid}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <AccountTypeSelect name='accountType' required={true} />
        {showOtherField && (
          <Controller
            name='other'
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label='Other'
                variant='bordered'
                isInvalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        )}
        <AddressForm required={true} name='address' />
      </div>

      {renderSectionDivider('Contact Information')}
      <div className='flex flex-col gap-4'>
        <Controller
          name='emailAddress'
          control={control}
          rules={{
            required: 'This field is required',
            pattern: { value: emailPattern, message: 'Invalid email' },
          }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label='Email'
              variant='bordered'
              type='email'
              isRequired
              isInvalid={fieldState.invalid}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <PhoneInput name='phoneNumber' label='Phone' control={control} required />
      </div>

      {renderSectionDivider('Office Meeting Info')}
      <div className='flex flex-col gap-4'>
        <p className='text-sm text-default-500'>
          If set, these fields are visible to reps on every meeting and availability slot for this
          account.
        </p>
        <Controller
          name='mealHeadcount'
          control={control}
          rules={{
            min: { value: 1, message: 'Headcount must be at least 1' },
          }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label='Meal Headcount'
              variant='bordered'
              type='number'
              min={1}
              value={field.value?.toString() ?? ''}
              onChange={event => {
                const parsed = Number.parseInt(event.target.value, 10);
                field.onChange(Number.isNaN(parsed) ? undefined : parsed);
              }}
              isInvalid={fieldState.invalid}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name='mealInstructions'
          control={control}
          rules={{
            maxLength: { value: 360, message: 'Instructions must be under 360 characters' },
          }}
          render={({ field, fieldState }) => (
            <Textarea
              {...field}
              label='Meal Instructions'
              variant='bordered'
              minRows={4}
              maxLength={360}
              description={`${mealInstructionsLength}/360`}
              isInvalid={fieldState.invalid}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </div>

      {renderSectionDivider('Specialties')}
      <div>
        <SpecialtiesSelect accountId={account?.id} name='specialties' />
      </div>
    </div>
  );
};

export default AccountFormFields;
